import { poucherApi } from 'services';
import { Auth } from 'models/auth';

const extendedApi = poucherApi.injectEndpoints({
  endpoints: (build) => ({
    lookup: build.query({
      transformResponse: (_response, meta) => ({ status: meta?.response?.status }),
      providesTags: ['Auth'],
      query: () => ({
        url: '/auth/lookup',
        credentials: 'include',
      }),
    }),

    signup: build.mutation({
      query: (params: Auth.SignupFormValues) => ({
        url: '/auth/signup',
        method: 'POST',
        body: params,
        credentials: 'include',
      }),
    }),

    resendConfirmationEmail: build.mutation({
      query: (email: string) => ({
        url: '/auth/resend-confirmation-email',
        method: 'POST',
        body: {
          email: email,
        },
      }),
    }),

    confirm: build.mutation({
      invalidatesTags: ['Profile'],
      query: (token: string) => ({
        url: '/auth/confirm',
        method: 'POST',
        body: { token },
      }),
    }),

    login: build.mutation({
      invalidatesTags: ['Auth'],
      query: (params: Auth.LoginFormValues) => ({
        url: '/auth/login',
        method: 'POST',
        body: params,
        credentials: 'include',
      }),
    }),

    // Phone sign-in / sign-up: text a code (behind the captcha), then check it. The check sets
    // the session cookie and creates the account when the number has none.
    phoneStart: build.mutation<Auth.PhoneStartResponse, Auth.PhoneStartValues>({
      query: (params) => ({
        url: '/auth/phone/start',
        method: 'POST',
        body: params,
        credentials: 'include',
      }),
    }),

    phoneVerify: build.mutation<Auth.PhoneVerifyResponse, Auth.PhoneVerifyValues>({
      // Only a right code signs in (a wrong one needs no session re-check)
      invalidatesTags: (_result, error) => (error ? [] : ['Auth']),
      query: (params) => ({
        url: '/auth/phone/verify',
        method: 'POST',
        body: params,
        credentials: 'include',
      }),
    }),

    forgotPassword: build.mutation({
      query: (params: Auth.ForgotPasswordFormValues) => ({
        url: '/auth/forgot-password',
        method: 'POST',
        body: params,
      }),
    }),

    recoverPassword: build.mutation({
      query: (params: Auth.RecoverPasswordFormValues) => ({
        url: '/auth/recover',
        method: 'POST',
        body: params,
      }),
    }),

    logout: build.query({
      query: () => ({
        url: '/auth/logout',
        credentials: 'include',
        method: 'DELETE',
      }),
    }),
  }),

  overrideExisting: false,
});

export const {
  useLookupQuery,
  useSignupMutation,
  useResendConfirmationEmailMutation,
  useConfirmMutation,
  useLoginMutation,
  usePhoneStartMutation,
  usePhoneVerifyMutation,
  useForgotPasswordMutation,
  useRecoverPasswordMutation,
  useLogoutQuery,
} = extendedApi;

export default extendedApi;
